import { CollectionAPI } from '@tacans/shared-marketplace-layer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    createCollectionsFilterFieldsPayload,
    mapCollectionsSorting,
} from 'src/features/common/explore/collections-api.utils';
import { CollectionsSort } from 'src/features/common/explore/ducks/collections/collections.types';
import { IExploreFilters } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.types';
import {
    FilterRender,
    ItemT,
} from 'src/features/white-labels/ocean/explore/containers/entity-filter-container/filter-render';
import { StyledNoFilterData } from 'src/features/white-labels/ocean/explore/containers/entity-filter-container/styles';
import { useDebouncedEffect } from 'src/hooks/use-debounced-effect';

const DEFAULT_PAGER = { limit: 10, page: 0 };

interface IProps {
    selected: string[];
    onChange: (key: keyof IExploreFilters, string: string[]) => void;
}

export const CollectionsFilterContainer: React.VFC<IProps> = ({ selected, onChange }) => {
    const { t } = useTranslation(['components']);
    const [showSearch, setShowSearch] = useState(false);
    const [total, setTotal] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [pager, setPager] = useState(DEFAULT_PAGER);
    const [data, setData] = useState<ItemT[]>([]);

    const fetchCollections = useCallback(
        (pager = DEFAULT_PAGER) => {
            setLoading(true);
            const payload = {
                sorting: mapCollectionsSorting(CollectionsSort.LATEST),
                filter: createCollectionsFilterFieldsPayload({
                    keywords: searchValue,
                }),
                pager: {
                    page: pager?.page ?? 0,
                    limit: pager?.limit ?? 20,
                },
            };
            return CollectionAPI.fetchByFilter(payload)
                .then((r) => {
                    setTotal(+(r.data.pager.totalCount ?? 0));
                    return r.data.collections?.map((el) => ({ id: el.id, name: el.name }));
                })
                .catch(() => [])
                .finally(() => setLoading(false));
        },
        [searchValue]
    );

    const loadMore = useCallback(async () => {
        const nextPager = {
            page: pager.page + 1,
            limit: 10,
        };
        setPager(nextPager);
        const items: ItemT[] = await fetchCollections(nextPager);
        setData((prev) => [...prev, ...(items || [])]);
    }, [fetchCollections, pager.page]);

    const init = useCallback(() => {
        fetchCollections().then((items) => {
            setData(items || []);
            if (items?.length > 5) {
                setShowSearch(true);
            }
        });
    }, [fetchCollections]);

    const search = useCallback(() => {
        setPager({
            page: 0,
            limit: 10,
        });
        fetchCollections().then((items) => {
            setData(items || []);
        });
    }, [fetchCollections]);

    const onSubmit = useCallback((e) => {
        setSearchValue(e.target.value);
    }, []);

    const handleSearch = useCallback((value: string) => {
        setSearchValue(value);
    }, []);

    const visibleItems = useMemo(() => {
        if (+pager.page === 0) {
            return data.slice(0, 6);
        }
        return data;
    }, [data, pager.page]);

    useDebouncedEffect(search, 500, [searchValue]);

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return data.length ? (
        <FilterRender
            showSearch={showSearch}
            onSubmit={onSubmit}
            data={visibleItems}
            total={total}
            loadMore={loadMore}
            onSearch={handleSearch}
            loading={loading}
            selected={selected}
            onFilterChange={onChange}
            filterName="collectionId"
        />
    ) : (
        <StyledNoFilterData size="sm" tag="div">
            {t('exploreFilter.filter.collections.noFound')}
        </StyledNoFilterData>
    );
};
